import { FC, useMemo } from "react"
import { Alert, Box, Button, MenuItem, Stack, TextField } from "@mui/material"
import { Add, Edit, Visibility } from "@mui/icons-material"
import { GridColDef } from "@mui/x-data-grid-pro"
import { useNavigate } from "react-router-dom"
import { DataGrid } from "@/core/components"
import { useUrlListState, useUrlFilters, UrlFilterSchema } from "@/core/hooks"
import { getApiErrorMessage } from "@/core/utils"
import { UserbotTemplates } from "../../interfaces"
import { useTemplatesQuery } from "../../store"
import { useUserbotWriteAccess } from "../../hooks"
import { TooltipAction } from "../common/tooltipAction"

const KIND_OPTIONS: { value: UserbotTemplates.TemplateKind; label: string }[] =
  [
    { value: "chain", label: "Цепочка" },
    { value: "trigger", label: "Триггер" },
    { value: "scheduled", label: "Расписание" },
  ]

const FILTERS_SCHEMA = {
  kind: "string",
  category: "string",
} as const satisfies UrlFilterSchema

export const TemplatesTable: FC = () => {
  const navigate = useNavigate()
  const canWrite = useUserbotWriteAccess()
  const { read: readFilters, write: writeFilters } =
    useUrlFilters(FILTERS_SCHEMA)
  const filters = readFilters()
  const list = useUrlListState()

  const { data, isFetching, isError, error } = useTemplatesQuery(
    {
      ...list.listArgs,
      // валидируем kind против KIND_OPTIONS — невалидное значение из URL не шлём
      kind: KIND_OPTIONS.some((o) => o.value === filters.kind)
        ? (filters.kind as UserbotTemplates.TemplateKind)
        : undefined,
      category: filters.category || undefined,
    },
    { pollingInterval: 60000 },
  )

  // Категории дропдауна: из загруженных страниц + значение из URL.
  // filter(Boolean) — nullable-категория не должна доехать до localeCompare.
  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...(filters.category ? [filters.category] : []),
            ...(data?.items ?? []).map((t) => t.category),
          ].filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [data, filters.category],
  )

  const setFilter = (patch: Partial<typeof filters>) => {
    writeFilters({ ...filters, ...patch })
    list.setPaginationModel({ page: 0, pageSize: list.pageSize })
  }

  const columns: GridColDef<UserbotTemplates.Template>[] = useMemo(
    () => [
      { field: "name", headerName: "Название", flex: 2, sortable: true },
      { field: "category", headerName: "Категория", flex: 2, sortable: false },
      {
        field: "kind",
        headerName: "Тип",
        flex: 1,
        sortable: false,
        valueGetter: ({ value }) =>
          KIND_OPTIONS.find((o) => o.value === value)?.label ?? value,
      },
      {
        field: "createdAt",
        headerName: "Создан",
        flex: 2,
        sortable: true,
        valueGetter: ({ value }) => new Date(value).toLocaleString("ru"),
      },
      {
        field: "updatedAt",
        headerName: "Обновлён",
        flex: 2,
        sortable: true,
        valueGetter: ({ value }) => new Date(value).toLocaleString("ru"),
      },
    ],
    [],
  )

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
      >
        <TextField
          select
          label="Тип"
          value={filters.kind ?? ""}
          onChange={(e) => setFilter({ kind: e.target.value })}
          sx={{ minWidth: 180 }}
          size="small"
        >
          <MenuItem value="">Все</MenuItem>
          {KIND_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Категория"
          value={filters.category ?? ""}
          onChange={(e) => setFilter({ category: e.target.value })}
          sx={{ minWidth: 180 }}
          size="small"
        >
          <MenuItem value="">Все</MenuItem>
          {categories.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>
        <Box flexGrow={1} />
        {canWrite && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/userbot/templates/new")}
          >
            Создать шаблон
          </Button>
        )}
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {getApiErrorMessage(error)}
        </Alert>
      )}

      <Box height="70vh">
        <DataGrid<UserbotTemplates.Template>
          columns={columns}
          rows={data?.items ?? []}
          getRowId={(row) => row.id}
          loading={isFetching && !data}
          rowCount={data?.total ?? 0}
          filterMode="client"
          {...list.gridProps}
          rowActions={[
            (r) => (
              <TooltipAction
                label="Открыть"
                icon={<Visibility />}
                onClick={() => navigate(`/userbot/templates/run/${r.row.id}`)}
              />
            ),
            ...(canWrite
              ? [
                  (r: { row: UserbotTemplates.Template }) => (
                    <TooltipAction
                      label="Редактировать"
                      icon={<Edit />}
                      onClick={() =>
                        navigate(`/userbot/templates/edit/${r.row.id}`)
                      }
                    />
                  ),
                ]
              : []),
          ]}
        />
      </Box>
    </Stack>
  )
}
